import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Timer } from "lucide-react";

/**
 * Compact live "time since punch-in" ticker for the mobile footer.
 *
 * - Polls /attendance/today once every 60s.
 * - When the employee has punched in but not out, renders a green pulse dot +
 *   HH:MM:SS elapsed. Ticks every second locally without hammering the API.
 * - When there's no active punch (or the day is closed), renders nothing so
 *   the bottom bar keeps its normal height.
 */
export default function PunchTicker() {
  const [today, setToday] = useState(null);
  const [now, setNow] = useState(() => new Date());

  // Poll attendance every minute
  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      try {
        const { data } = await api.get("/attendance/today");
        if (!cancelled) setToday(data);
      } catch {
        // silent — the middle-of-page dashboard will surface real errors
      }
    };
    refresh();
    const id = setInterval(refresh, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Tick every second for the live counter
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const punchIn = today?.punch_in_time;
  const punchOut = today?.punch_out_time;
  if (!punchIn || punchOut) return null;

  const start = new Date(punchIn).getTime();
  const diff = Math.max(0, now.getTime() - start);
  const h = Math.floor(diff / 3_600_000);
  const m = Math.floor((diff % 3_600_000) / 60_000);
  const s = Math.floor((diff % 60_000) / 1000);
  const formatted = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;

  return (
    <div
      data-testid="punch-ticker"
      className="max-w-md mx-auto flex items-center justify-between px-4 py-1.5 border-b border-border bg-emerald-500/10"
    >
      <div className="flex items-center gap-2 text-emerald-300">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
        <Timer className="h-3.5 w-3.5" strokeWidth={1.75} />
        <span className="label-mono">On the clock</span>
      </div>
      <span
        data-testid="footer-timer"
        className="font-mono-brand tabular-nums text-emerald-300 text-sm"
      >
        {formatted}
      </span>
    </div>
  );
}
